import React from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Player, PlayerWithLeagueContext } from '../generated-api';

export interface Team {
  id: string;
  name: string;
  color: string;
  playerIds: string[];
}

export interface RankingEntity {
  id: string; // Either playerId or teamId
  type: 'player' | 'team';
  position: number;
  tiedWith: string[];
}

export interface SortableEntityProps {
  entity: RankingEntity;
  teams: Team[];
  allPlayers: (Player | PlayerWithLeagueContext)[];
  allEntities: RankingEntity[];
  onSeparateFromTie: (entityId: string) => void;
  onCreateTie: (entityId1: string, entityId2: string) => void;
  isDraggedOver?: boolean;
  onMoveUp?: (entityId: string) => void;
  onMoveDown?: (entityId: string) => void;
}

// Helper function to get position display with styled badges for top 3
const getPositionDisplay = (position: number): React.ReactNode => {
  switch (position) {
    case 1:
      return (
        <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-amber-400 text-amber-900 text-sm font-bold" aria-label="1st place">
          1st
        </span>
      );
    case 2:
      return (
        <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-stone-300 text-stone-700 text-sm font-bold" aria-label="2nd place">
          2nd
        </span>
      );
    case 3:
      return (
        <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-orange-700/20 text-orange-800 text-sm font-bold" aria-label="3rd place">
          3rd
        </span>
      );
    default:
      return (
        <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-stone-100 text-stone-500 text-sm font-bold">
          #{position}
        </span>
      );
  }
};

export const TEAM_ANIMAL_NAMES = [
  'Bears', 'Wolves', 'Eagles', 'Hawks', 'Lions',
  'Tigers', 'Foxes', 'Owls', 'Sharks', 'Panthers',
  'Falcons', 'Ravens', 'Cobras', 'Dolphins', 'Lynxes',
  'Otters', 'Badgers', 'Stallions', 'Jaguars', 'Crows',
];

export const TEAM_COLORS = [
  { name: 'Red', bg: 'bg-red-100', border: 'border-red-300', text: 'text-red-800' },
  { name: 'Blue', bg: 'bg-primary-100', border: 'border-primary-300', text: 'text-primary-800' },
  { name: 'Yellow', bg: 'bg-yellow-100', border: 'border-yellow-300', text: 'text-yellow-800' },
  { name: 'Black', bg: 'bg-surface-subtle', border: 'border-edge-input', text: 'text-gray-800' },
  { name: 'Pink', bg: 'bg-pink-100', border: 'border-pink-300', text: 'text-pink-800' },
  { name: 'Purple', bg: 'bg-purple-100', border: 'border-purple-300', text: 'text-purple-800' },
  { name: 'White', bg: 'bg-surface', border: 'border-edge-input', text: 'text-gray-800' },
  { name: 'Green', bg: 'bg-green-100', border: 'border-green-300', text: 'text-green-800' },
];

const SortableEntity: React.FC<SortableEntityProps> = ({
  entity,
  teams,
  allPlayers,
  allEntities,
  onSeparateFromTie,
  onCreateTie,
  isDraggedOver,
  onMoveUp,
  onMoveDown,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: entity.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const isTied = entity.tiedWith.length > 0;

  // Get adjacent entities for tie creation
  const sortedEntities = allEntities.sort((a, b) => a.position - b.position);
  const currentIndex = sortedEntities.findIndex(e => e.id === entity.id);
  const previousEntity = currentIndex > 0 ? sortedEntities[currentIndex - 1] : null;
  const nextEntity = currentIndex < sortedEntities.length - 1 ? sortedEntities[currentIndex + 1] : null;

  // Handle keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.shiftKey && e.key === 'ArrowUp' && onMoveUp) {
      e.preventDefault();
      onMoveUp(entity.id);
    } else if (e.shiftKey && e.key === 'ArrowDown' && onMoveDown) {
      e.preventDefault();
      onMoveDown(entity.id);
    } else if ((e.key === 't' || e.key === 'T') && !e.shiftKey && !e.ctrlKey && !e.metaKey) {
      e.preventDefault();
      if (isTied) {
        onSeparateFromTie(entity.id);
      } else if (nextEntity) {
        onCreateTie(entity.id, nextEntity.id);
      } else if (previousEntity) {
        onCreateTie(entity.id, previousEntity.id);
      }
    }
  };

  if (entity.type === 'team') {
    const team = teams.find(t => t.id === entity.id);
    if (!team) return null;

    const teamColor = TEAM_COLORS.find(color => color.name.toLowerCase() === team.color);

    return (
      <div
        ref={setNodeRef}
        style={style}
        {...attributes}
        tabIndex={0}
        onKeyDown={handleKeyDown}
        className={`p-3 rounded-xl border transition-all duration-fast ${
          isTied
            ? 'border-l-4 border-l-warning-500 bg-warning-50 border-warning-300'
            : teamColor
            ? `${teamColor.bg} ${teamColor.border}`
            : 'bg-surface-base border-edge-input'
        } ${isDragging ? 'shadow-lg' : 'shadow-sm'} ${
          isDraggedOver ? 'ring-2 ring-primary-400 border-primary-400' : ''
        } focus:outline-none focus:ring-2 focus:ring-primary-500`}
      >
        <div className="flex items-center" {...listeners}>
          <div className="flex flex-col gap-[3px] mr-1 opacity-60 cursor-move" aria-hidden="true">
            <div className="flex gap-[3px]">
              <div className="w-[4px] h-[4px] rounded-full bg-current" />
              <div className="w-[4px] h-[4px] rounded-full bg-current" />
            </div>
            <div className="flex gap-[3px]">
              <div className="w-[4px] h-[4px] rounded-full bg-current" />
              <div className="w-[4px] h-[4px] rounded-full bg-current" />
            </div>
            <div className="flex gap-[3px]">
              <div className="w-[4px] h-[4px] rounded-full bg-current" />
              <div className="w-[4px] h-[4px] rounded-full bg-current" />
            </div>
          </div>
          <div className="flex-shrink-0 flex items-center gap-2 mx-2">
            {getPositionDisplay(entity.position)}
            {isTied && (
              <span className="text-xs bg-warning-100 text-warning-700 px-2.5 py-1 rounded-full font-medium">
                TIE
              </span>
            )}
          </div>
          <div className="min-w-0">
            <div className={`font-medium ${teamColor?.text || 'text-heading'}`}>
              {team.name}
            </div>
            <div className="text-sm opacity-75 truncate">
              {team.playerIds.map(playerId => {
                const player = allPlayers.find(p => p.id === playerId);
                return player?.name;
              }).filter(Boolean).join(', ')}
            </div>
          </div>
        </div>
      </div>
    );
  } else {
    // Individual player
    const player = allPlayers.find(p => p.id === entity.id);
    if (!player) return null;

    return (
      <div
        ref={setNodeRef}
        style={style}
        {...attributes}
        tabIndex={0}
        onKeyDown={handleKeyDown}
        className={`p-3 rounded-xl border transition-all duration-fast ${
          isTied
            ? 'border-l-4 border-l-warning-500 bg-warning-50 border-warning-300'
            : 'bg-surface border-edge hover:border-edge-input'
        } ${isDragging ? 'shadow-lg' : 'shadow-sm'} ${
          isDraggedOver ? 'ring-2 ring-primary-400 border-primary-400' : ''
        } focus:outline-none focus:ring-2 focus:ring-primary-500`}
      >
        <div className="flex items-center" {...listeners}>
          <div className="flex flex-col gap-[3px] mr-1 opacity-60 cursor-move" aria-hidden="true">
            <div className="flex gap-[3px]">
              <div className="w-[4px] h-[4px] rounded-full bg-current" />
              <div className="w-[4px] h-[4px] rounded-full bg-current" />
            </div>
            <div className="flex gap-[3px]">
              <div className="w-[4px] h-[4px] rounded-full bg-current" />
              <div className="w-[4px] h-[4px] rounded-full bg-current" />
            </div>
            <div className="flex gap-[3px]">
              <div className="w-[4px] h-[4px] rounded-full bg-current" />
              <div className="w-[4px] h-[4px] rounded-full bg-current" />
            </div>
          </div>
          <div className="flex-shrink-0 flex items-center gap-2 mx-2">
            {getPositionDisplay(entity.position)}
            {isTied && (
              <span className="text-xs bg-warning-100 text-warning-700 px-2.5 py-1 rounded-full font-medium">
                TIE
              </span>
            )}
          </div>
          <div className="flex-shrink-0">
            {player.imageUrl ? (
              <img
                src={player.imageUrl}
                alt={player.name}
                className="w-8 h-8 rounded-full object-cover border-2 border-white shadow-sm"
              />
            ) : (
              <div className="w-8 h-8 rounded-full bg-surface-skeleton flex items-center justify-center border-2 border-white shadow-sm">
                <span className="text-xs font-medium text-body">
                  {player.name?.charAt(0).toUpperCase()}
                </span>
              </div>
            )}
          </div>
          <div className="ml-2 min-w-0">
            <div className="font-medium text-heading truncate">{player.name}</div>
          </div>
        </div>
      </div>
    );
  }
};

export default SortableEntity;